




































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import { ApiState, SortOrder } from '@/store/types/general.types';
import {
  GroupDetail,
  Pagination
} from '@/store/modules/admin/types/admin.types';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import {
  AddMembersToGroupErrorResponsePayload,
  AddMembersToGroupRequestPayload,
  DeleteGroupMemberRequestPayload,
  FilteredGroupMemberParamsPayload,
  UpdateGroupMemberRequestPayload
} from '@/store/modules/admin/types/group.types';
import {
  GroupUserAttributeTypeStatus,
  MyjbiGroupDetail,
  MyjbiGroupMember,
  MyjbiGroupUserAttributeSpec
} from '@/jbi-shared/types/myjbi-group.types';
import { fromPairs, uniq } from 'lodash';
import {
  PermissionsMatrixActionsEnum,
  ResourceExceptions
} from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { isUserAllowed } from '@/utils/rbac.util';
import { EntityTypes } from '@/store/modules/module-tree/enums/module-tree.enums';
import MemberSection from '@/views/AdminViewGroup/components/MemberSection.vue';
import { MemberObject, userAttributesArrWithValues } from '@/utils/group.util';
import { ToastProgrammatic } from 'buefy';
import LicenseSeatsConflictModal from '../AdminViewGroup/components/LicenseSeatsConflictModal.vue';
import LicenseFormModal from '../AdminGroupManagement/components/LicenseFormModal.vue';
import {
  GroupLicense,
  UpdateGroupLicenseRequestPayload
} from '@/store/modules/admin/types/group-license.types';
import { AxiosError } from 'axios';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { subscribeToRealTime } from '@/utils/real-time.util';
import { useWebsocket } from '@/utils/websocket.util';

@Component({
  components: {
    BasePaginatorHoc,
    MemberSection
  }
})
export default class GroupMembersTab extends Vue {
  @Prop(Number) groupId!: number;

  @Action('admin/getMembers')
  getMembers!: (params: {
    groupId: number;
    options: FilteredGroupMemberParamsPayload;
  }) => Promise<Pagination<MyjbiGroupMember>>;

  @State(({ admin }: RootState) => admin.apiState.getMembers)
  getMembersState!: ApiState;

  @State((state: RootState) => state.admin.members)
  membersDetails!: Pagination<MyjbiGroupMember>;

  @Action('admin/getGroupDetail')
  getGroupDetail!: (params: { id: number; isMembers: boolean }) => void;

  @State(({ admin }: RootState) => admin.apiState.getGroupDetail)
  getGroupDetailState!: ApiState;

  @State(({ admin }: RootState) => admin.groupDetail)
  groupDetail!: GroupDetail;

  @Action('rolesAndPermissions/getGroupExceptions')
  getGroupExceptions!: (groupId: number) => Promise<ResourceExceptions>;

  @State(
    ({ rolesAndPermissions }: RootState) =>
      rolesAndPermissions.apiState.getGroupExceptions
  )
  getGroupExceptionsState!: ApiState;

  @State((state: RootState) => state.rolesAndPermissions.groupExceptions)
  exceptions!: ResourceExceptions;

  @Action('admin/getParentGroupDetailByChildGroupId')
  getParentGroupDetailByChildGroupId!: (
    id: number
  ) => Promise<MyjbiGroupDetail>;

  @State(
    ({ admin }: RootState) => admin.apiState.getParentGroupDetailByChildGroupId
  )
  getParentGroupDetailByChildGroupIdState!: ApiState;

  @Action('admin/addMembersToGroup')
  addMembersToGroup!: (
    payload: AddMembersToGroupRequestPayload
  ) => Promise<void>;

  @Action('admin/editGroupMember')
  editGroupMember!: (payload: UpdateGroupMemberRequestPayload) => Promise<void>;

  @Action('admin/deleteGroupMember')
  deleteGroupMember!: (
    payload: DeleteGroupMemberRequestPayload
  ) => Promise<void>;

  @Action('admin/updateGroupLicense')
  updateGroupLicense!: (
    options: UpdateGroupLicenseRequestPayload
  ) => Promise<GroupLicense>;

  @Action('admin/getMasterGroupEmailDomains')
  getMasterGroupEmailDomains!: (id: number) => Promise<string[]>;

  // pagination
  public perPage = 50;
  public page = 1;
  public sortColumn: string = '';
  public sortOrder: SortOrder = SortOrder.ASC;
  public totalItems: number = 0;

  // filter
  public filteredMemberParams = this.getInitFormParams();

  // params
  public memberSectionKey: number = Math.floor(Math.random() * 999);
  public allMembers: MyjbiGroupMember[] = [];
  public groupUserAttributeSpecs: MyjbiGroupUserAttributeSpec[] = [];
  public groupTypeName: string = '';
  public groupExceptions: ResourceExceptions = { granted: [], blocked: [] };
  private unsubscribeRealtime: (() => void) | undefined;

  get parentGroupDetailByChildGroupId() {
    return (this.$store.state as RootState).admin
      .parentGroupDetailByChildGroupId;
  }

  get attributeOrder() {
    return (this.$store.state as RootState).admin.groupAttributeOrder;
  }

  get addMembersToGroupSuccess() {
    return (this.$store.state as RootState)?.admin?.apiState?.addMembersToGroup
      .success;
  }

  get editGroupMemberSuccess() {
    return (this.$store.state as RootState)?.admin?.apiState?.editGroupMember
      .success;
  }

  get deleteGroupMemberSuccess() {
    return (this.$store.state as RootState)?.admin?.apiState?.deleteGroupMember
      .success;
  }

  get masterGroupEmailDomains() {
    return (this.$store.state as RootState).admin.masterGroupEmailDomains;
  }

  get allAttributeSpecs() {
    if (this.groupUserAttributeSpecs) {
      const allAttributeSpecs: MyjbiGroupUserAttributeSpec[] = [
        ...this.groupUserAttributeSpecs,
        {
          groupUserAttribute: {
            id: 0,
            name: 'Email Verified',
            slug: 'user-profile-email-verified-1001',
            option: null,
            status: GroupUserAttributeTypeStatus.ACTIVE,
            groupUserAttributeType: {
              id: 1,
              type: 'text',
              option: null,
              status: GroupUserAttributeTypeStatus.ACTIVE
            }
          },
          required: false,
          isDefault: true
        },
        {
          groupUserAttribute: {
            id: 0,
            name: 'User Status',
            slug: 'user-profile-user-status-1002',
            option: null,
            status: GroupUserAttributeTypeStatus.ACTIVE,
            groupUserAttributeType: {
              id: 1,
              type: 'text',
              option: null,
              status: GroupUserAttributeTypeStatus.ACTIVE
            }
          },
          required: false,
          isDefault: true
        },
        {
          groupUserAttribute: {
            id: 0,
            name: 'Username',
            slug: 'user-profile-username-1003',
            option: null,
            status: GroupUserAttributeTypeStatus.ACTIVE,
            groupUserAttributeType: {
              id: 1,
              type: 'text',
              option: null,
              status: GroupUserAttributeTypeStatus.ACTIVE
            }
          },
          required: false,
          isDefault: true
        }
      ];

      if (this.attributeOrder?.attributeOrder) {
        const newAttributesOrder: string[] = [];
        const existingAttributeOrder: string[] = this.attributeOrder
          .attributeOrder;
        allAttributeSpecs.forEach((attribute) => {
          const { slug } = attribute.groupUserAttribute;
          if (!existingAttributeOrder.includes(slug)) {
            newAttributesOrder.push(slug);
          }
        });
        const order = uniq([...existingAttributeOrder, ...newAttributesOrder]);

        return allAttributeSpecs.sort(
          (a, b) =>
            order.indexOf(a.groupUserAttribute.slug) -
            order.indexOf(b.groupUserAttribute.slug)
        );
      }

      return allAttributeSpecs;
    }
  }

  get MemberSection() {
    return MemberSection;
  }

  get PermissionsMatrixActionsEnum() {
    return PermissionsMatrixActionsEnum;
  }

  get allowedEmailDomains() {
    // get email domains from current group details
    const emailDomainFromCurrentId = this.groupDetail?.emailDomains;

    // get email domains from root ancestor
    const emailDomainFromAncestor = this.masterGroupEmailDomains;

    // if this group detail has email domains, use that
    if (emailDomainFromCurrentId && emailDomainFromCurrentId.length > 0) {
      return emailDomainFromCurrentId;
      // if not, use the one you get from root ancestor (master group)
    } else if (emailDomainFromAncestor && emailDomainFromAncestor.length > 0) {
      return emailDomainFromAncestor;
    }
  }

  @Watch('getMembersState')
  onGetMembersState() {
    if (
      !this.getMembersState ||
      this.getMembersState.error ||
      this.getMembersState.loading ||
      !this.getMembersState.success ||
      !this.membersDetails
    ) {
      return;
    }

    this.allMembers = this.membersDetails.items;
    this.totalItems = this.membersDetails.totalItems;
  }

  @Watch('getGroupDetailState')
  onGetGroupDetailState() {
    if (
      !this.getGroupDetailState ||
      this.getGroupDetailState.error ||
      this.getGroupDetailState.loading ||
      !this.getGroupDetailState.success ||
      !this.groupDetail ||
      !this.groupDetail.groupUserAttributeSpecs
    ) {
      return;
    }

    this.groupUserAttributeSpecs = this.groupDetail.groupUserAttributeSpecs;
    this.groupTypeName = this.groupDetail.types[0];
  }

  @Watch('deleteGroupMemberSuccess')
  @Watch('editGroupMemberSuccess')
  @Watch('addMembersToGroupSuccess')
  @isTruthy
  onMembersChangedSuccess() {
    this.handleResetQuery();
  }

  @Watch('getGroupExceptionsState')
  onGetGroupExceptionsState() {
    if (
      !this.getGroupExceptionsState ||
      this.getGroupExceptionsState.error ||
      this.getGroupExceptionsState.loading ||
      !this.getGroupExceptionsState.success ||
      !this.exceptions
    ) {
      return;
    }

    this.groupExceptions = this.exceptions;
  }

  isUserAllowed(
    action: PermissionsMatrixActionsEnum,
    module: string,
    skipImplicitCheck?: boolean
  ): boolean {
    const instance = EntityTypes.GROUP + '_' + this.groupId;
    return isUserAllowed(
      action,
      module,
      EntityTypes.GROUP,
      this.groupTypeName,
      this.groupId,
      this.groupExceptions,
      skipImplicitCheck
    );
  }

  getInitFormParams(): any {
    return fromPairs(
      this.allAttributeSpecs?.map((attribute) => [
        attribute?.groupUserAttribute?.slug,
        ''
      ])
    );
  }

  async mounted() {
    this.filteredMemberParams = this.getInitFormParams();
    const queryParams = this.$route.query;
    let queryFilter = this.filteredMemberParams;
    if (queryParams && Object.keys(queryParams).length) {
      Object.keys(queryParams).forEach((each) => {
        if (Object.keys(this.filteredMemberParams).includes(each)) {
          this.filteredMemberParams[each] = queryParams[each];
          return (queryFilter[each] = queryParams[each]);
        }
      });
    } else {
      queryFilter = this.filteredMemberParams;
    }

    this.$router.push({
      path: this.$route.path,
      query: {
        ...queryFilter,
        limit: this.perPage.toString(),
        page: this.page.toString(),
        sortColumn: this.sortColumn,
        sortOrder: this.sortOrder
      }
    });
    this.perPage = +this.$route.query.limit || this.perPage;
    this.page = +this.$route.query.page || this.page;

    this.handleResetQuery();
    this.getGroupDetail({ id: this.groupId, isMembers: false });
    this.getGroupExceptions(this.groupId);
    if (
      this.isUserAllowed(
        PermissionsMatrixActionsEnum.READ,
        'group_administration-groups-read_groups-read_email_domain',
        true
      )
    ) {
      this.getMasterGroupEmailDomains(this.groupId);
    }

    if (this.groupId) {
      this.unsubscribeRealtime = await subscribeToRealTime.call(
        this,
        'groupMember-' + this.groupId,
        async () => {
          this.handleResetQuery();
        }
      );
    }
  }

  beforeDestroy() {
    if (this.unsubscribeRealtime) {
      this.unsubscribeRealtime(); // remove listener
      const { disconnectWs } = useWebsocket.call(this);
      disconnectWs();
    }
  }

  resetFilter() {
    this.filteredMemberParams = this.getInitFormParams();
  }

  async handlePaginator({ perPage, page }: { perPage: number; page: number }) {
    this.filteredMemberParams = this.getInitFormParams();
    const queryParams = this.$route.query;
    const queryFilter = this.filteredMemberParams;
    if (queryParams && Object.keys(queryParams).length) {
      Object.keys(queryParams).forEach((each) => {
        if (Object.keys(this.filteredMemberParams).includes(each)) {
          this.filteredMemberParams[each] = queryParams[each];
          return (queryFilter[each] = queryParams[each]);
        }
      });
    }

    this.perPage = perPage || +this.$route.query.limit;
    this.page = page || +this.$route.query.page;

    await this.getMembers({
      groupId: this.groupId,
      options: {
        ...this.filteredMemberParams,
        limit: this.perPage,
        page: this.page,
        sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
        sortOrder:
          (this.$route.query.sortOrder as 'ASC' | 'DESC') || this.sortOrder
      }
    });
    this.$router.push({
      path: this.$route.path,
      query: {
        ...this.filteredMemberParams,
        limit: this.perPage.toString(),
        page: this.page.toString(),
        sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
        sortOrder:
          (this.$route.query.sortOrder as 'ASC' | 'DESC') || this.sortOrder
      }
    });
  }

  async handleSort(data: any) {
    this.sortColumn = data.sortColumn;
    this.sortOrder = data.sortOrder;

    const params = {
      ...this.filteredMemberParams,
      limit: +this.$route.query.limit || this.perPage,
      page: +this.$route.query.page || this.page,
      sortColumn: this.sortColumn,
      sortOrder: this.sortOrder
    };

    await this.getMembers({ groupId: this.groupId, options: params });
    this.$router.push({
      path: this.$route.path,
      query: {
        ...this.filteredMemberParams,
        limit: (this.$route.query.limit as string) || this.perPage.toString(),
        page: (this.$route.query.page as string) || this.page.toString(),
        sortColumn: this.sortColumn,
        sortOrder: this.sortOrder
      }
    });
  }

  async handleFilter() {
    this.perPage = +this.$route.query.limit;
    const queryParams = this.$route.query;
    const queryFilter = this.getInitFormParams();
    Object.keys(queryParams).forEach((each) => {
      if (Object.keys(this.filteredMemberParams).includes(each)) {
        return (queryFilter[each] = queryParams[each]);
      }
    });
    if (
      JSON.stringify(queryFilter) !== JSON.stringify(this.filteredMemberParams)
    ) {
      this.page = 1;
      await this.getMembers({
        groupId: this.groupId,
        options: {
          ...this.filteredMemberParams,
          limit: +this.$route.query.limit || this.perPage,
          page: 1,
          sortColumn:
            (this.$route.query.sortColumn as string) || this.sortColumn,
          sortOrder:
            (this.$route.query.sortOrder as 'ASC' | 'DESC') || this.sortOrder
        }
      });
      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.filteredMemberParams,
          limit: (this.$route.query.limit as string) || this.perPage.toString(),
          page: (this.$route.query.page as string) || this.page.toString(),
          sortColumn:
            (this.$route.query.sortColumn as string) || this.sortColumn,
          sortOrder:
            (this.$route.query.sortOrder as 'ASC' | 'DESC') || this.sortOrder
        }
      });
    }
  }

  async handleResetQuery() {
    await this.getMembers({
      groupId: this.groupId,
      options: {
        ...this.filteredMemberParams,
        limit: +this.$route.query.limit || this.perPage,
        page: +this.$route.query.page || this.page,
        sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
        sortOrder:
          (this.$route.query.sortOrder as 'ASC' | 'DESC') || this.sortOrder
      }
    });
  }

  async handleAddMember({
    values,
    notify
  }: {
    values: MemberObject;
    notify: boolean;
  }) {
    try {
      await this.addMembersToGroup({
        groupId: this.groupDetail?.id!,
        userAttributesArr:
          this.allAttributeSpecs && this.allAttributeSpecs.length > 0
            ? userAttributesArrWithValues([values], this.allAttributeSpecs)
            : [],
        notify
      });
      ToastProgrammatic.open({
        queue: true,
        type: 'is-dark',
        position: 'is-top',
        message: `1 Member(s) added`
      });
    } catch (error: any) {
      this.handleAddMemberError(error);
    }
  }

  async handleEditMember({
    memberObj,
    userId
  }: {
    memberObj: { values: MemberObject };
    userId: number;
  }) {
    try {
      await this.editGroupMember({
        groupId: this.groupDetail?.id!,
        userId,
        userAttributesArr:
          this.allAttributeSpecs && this.allAttributeSpecs.length > 0
            ? userAttributesArrWithValues(
                [memberObj.values],
                this.allAttributeSpecs
              )
            : []
      });
    } catch (error: any) {
      ToastProgrammatic.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message: error?.response?.data?.message || error,
        duration: 5000
      });
    }
  }

  async handleDeleteMember(userId: number) {
    try {
      await this.deleteGroupMember({
        groupId: this.groupDetail?.id!,
        userId
      });
    } catch (error: any) {
      ToastProgrammatic.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message: error?.response?.data?.message || error,
        duration: 5000
      });
    }
  }

  async handleAddMultipleMembers({
    values,
    notify
  }: {
    values: MemberObject[];
    notify: boolean;
  }) {
    try {
      await this.addMembersToGroup({
        groupId: this.groupDetail?.id!,
        userAttributesArr:
          values && this.allAttributeSpecs
            ? userAttributesArrWithValues(values, this.allAttributeSpecs)
            : [],
        notify
      });

      ToastProgrammatic.open({
        queue: true,
        type: 'is-dark',
        position: 'is-top',
        message: `${values?.length} Member(s) added`
      });
    } catch (error: any) {
      this.handleAddMemberError(error);
    }
  }

  async handleAddFromParent({
    values,
    notify
  }: {
    values: MemberObject[];
    notify: boolean;
  }) {
    try {
      await this.addMembersToGroup({
        groupId: this.groupDetail?.id!,
        userAttributesArr:
          values && this.allAttributeSpecs
            ? userAttributesArrWithValues(values, this.allAttributeSpecs)
            : [],
        notify
      });

      ToastProgrammatic.open({
        queue: true,
        type: 'is-dark',
        position: 'is-top',
        message: `${values?.length} Member(s) added`
      });
    } catch (error: any) {
      this.handleAddMemberError(error);
    }
  }

  async handleAddExistingMembers({
    values,
    notify
  }: {
    values: MemberObject[];
    notify: boolean;
  }) {
    try {
      await this.addMembersToGroup({
        groupId: this.groupDetail?.id!,
        userAttributesArr:
          values && this.allAttributeSpecs
            ? userAttributesArrWithValues(values, this.allAttributeSpecs)
            : [],
        notify
      });

      ToastProgrammatic.open({
        queue: true,
        type: 'is-dark',
        position: 'is-top',
        message: `${values?.length} Member(s) added`
      });
    } catch (error: any) {
      this.handleAddMemberError(error);
    }
  }

  handleAddMemberError(error: AxiosError) {
    if (error?.response?.status === 409) {
      this.openLicenseSeatsConflictModal(
        error.response?.data as AddMembersToGroupErrorResponsePayload
      );
    } else {
      const data = error.response?.data as { message?: string } | undefined;
      ToastProgrammatic.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message: data?.message ?? String(error),
        duration: 5000
      });
    }
  }

  openLicenseSeatsConflictModal(
    errorResponse: AddMembersToGroupErrorResponsePayload
  ) {
    const { seats, groupProductName, groupName } = errorResponse;
    const { close } = this.$buefy.modal.open({
      parent: this,
      component: LicenseSeatsConflictModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        groupProductName,
        groupName,
        seats
      },
      events: {
        open: () => {
          this.openEditLicenseModal(errorResponse, close);
        }
      }
    });
  }

  openEditLicenseModal(
    errorResponse: AddMembersToGroupErrorResponsePayload,
    closeLicenseSeatsConflictModal: () => void
  ) {
    const { close } = this.$buefy.modal.open({
      parent: this,
      component: LicenseFormModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        id: errorResponse.id,
        name: errorResponse.name,
        startAt: errorResponse.startAt,
        endAt: errorResponse.endAt,
        seats: errorResponse.seats,
        groupMemberCount: errorResponse.groupMemberCount,
        groupId: errorResponse.groupId,
        groupProductId: errorResponse.groupProductId
      },
      events: {
        submit: async (v: UpdateGroupLicenseRequestPayload) => {
          await this.handleEditLicense(v);
          close();
          closeLicenseSeatsConflictModal();

          ToastProgrammatic.open({
            queue: true,
            type: 'is-dark',
            position: 'is-top',
            message: `Changes saved`
          });
        }
      }
    });
  }

  async handleEditLicense(values: UpdateGroupLicenseRequestPayload) {
    try {
      await this.updateGroupLicense(values);
    } catch (error: any) {
      ToastProgrammatic.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message: error?.response?.data?.message || error,
        duration: 5000
      });
    }
  }
}
